import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from '../_services/auth.service';
import { AlertService } from '../_services/alert.service';
import { environment } from 'src/environments/environment';
const { expiryTokenTime } = environment; 

@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  private readonly INACTIVITY_LIMIT = expiryTokenTime; // 30 minutes in milliseconds
  private inactivityTimeout: any;
  private countdownInterval: any;
  private routeSubscription: Subscription | null = null;
  private timeLeft = new BehaviorSubject<string>('30:00');
  timeLeft$ = this.timeLeft.asObservable();

  constructor(
    private router: Router,
    private authService: AuthService,
    private alertService: AlertService
  ) {}

  // Manually start the inactivity timer
  startInactivityTracking(): void {
    // console.log("Starting Inactivity Timer");
    this.setupRouteListener();
    this.updateLastActivity();
  }

  // Manually stop the inactivity timer
  stopInactivityTracking(): void {
    // console.log("Stopping Inactivity Timer");
    this.clearTimers();  // Only clear timers, not storage
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  // Track user route changes
  private setupRouteListener(): void {
    this.routeSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateLastActivity();
      });
  }

  // Update the last activity timestamp in localStorage
  updateLastActivity(): void {
    localStorage.setItem('lastActivity', Date.now().toString());
    this.resetInactivityTimer();
    this.startCountdown();
  }

  // Initialize or reset the inactivity timer
  private resetInactivityTimer(): void {
    clearTimeout(this.inactivityTimeout);
    this.inactivityTimeout = setTimeout(() => this.checkInactivity(), this.INACTIVITY_LIMIT);
  }

  // Initialize the countdown display
  private startCountdown(): void {
    clearInterval(this.countdownInterval);
    let remainingTime = this.INACTIVITY_LIMIT / 1000; // seconds

    this.countdownInterval = setInterval(() => {
      if (remainingTime <= 0) {
        // console.log("Logout Due to Inactivity (Countdown Timer)");
        // Don't clear storage here - let logout handle it after logging
        this.stopInactivityTracking();
        this.authService.logout('inactivity');  // Pass 'inactivity' as reason
        this.alertService.toastWarringMessageAlertWithMessage("Logout Due to Inactivity");
      } else {
        remainingTime--;
        const minutes = Math.floor(remainingTime / 60).toString().padStart(2, '0');
        const seconds = (remainingTime % 60).toString().padStart(2, '0');
        this.timeLeft.next(`${minutes}:${seconds}`);
      }
    }, 1000);
  }

  // Check for inactivity
  private checkInactivity(): void {
    const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0', 10);
    const currentTime = Date.now();
    
    if (currentTime - lastActivity > this.INACTIVITY_LIMIT) {
      // console.log("Logout Due to Inactivity (Inactivity Check)");
      this.stopInactivityTracking();
      
      // For inactivity logout, clean storage immediately to avoid race condition
      // where login component checks isLoggedIn() before AuthService.logout() completes
      localStorage.clear();
      sessionStorage.clear();
      
      // Navigate directly to login with inactivity parameter
      this.router.navigate(['/auth/login'], { queryParams: { reason: 'inactivity' } });
      
      // Still call logout for logging purposes, but don't rely on it for navigation
      this.authService.logout('inactivity');  // Pass 'inactivity' as reason
      this.alertService.toastWarringMessageAlertWithMessage("Logout Due to Inactivity");
    } else {
      this.resetInactivityTimer(); // Keep checking
      this.startCountdown();
    }
  }

  // Clear localStorage and timers
  private clearLocalStorage(): void {
    localStorage.clear();
    this.clearTimers();
    this.timeLeft.next("30:00");
  }

  // Clear only timers without touching storage
  private clearTimers(): void {
    clearTimeout(this.inactivityTimeout);
    clearInterval(this.countdownInterval);
    this.timeLeft.next("30:00");
  }
}
